/**
 *
 * Created by neo on 18.01.17.
 */

import { makeAutoObservable } from 'mobx';
import { LocalizedValue, createLocalizedValue } from './LocalizedValue';
import { Audited, createAuditedFields } from './Audited';
import { languageState } from '../Contexts/LanguageContext';

export const languagePriority = ['de', 'en', 'fr', 'it'];

// Observable model type
export type LocalizedNamedEntity = Audited & {
  name: LocalizedValue[];
  readonly defaultName: string;
  readonly translatedName: string;
  changeName(lang: string, value: string): void;
  getName(lang: string): string;
  nameMatches(query?: string): boolean;
};

// Factory function
export function createLocalizedNamedEntity(json?: Partial<LocalizedNamedEntity>): LocalizedNamedEntity {
  const audited = createAuditedFields(json);

  return makeAutoObservable({
    ...audited,
    name: (json?.name || []).map((l) => createLocalizedValue(l)),

    get defaultName(): string {
      for (const lang of languagePriority) {
        const entry = this.name.find((l) => l.lang === lang);
        if (entry) {
          return entry.value ?? '';
        }
      }
      const first = this.name[0];
      return first?.value ?? '';
    },

    get translatedName(): string {
      const language = languageState.language;
      const langFit = this.name.find((n) => n.lang === language.toLowerCase())?.value;

      if (langFit?.trim()) {
        return langFit;
      }

      return this.defaultName;
    },

    changeName(lang: string, value: string) {
      const existing = this.name.find((n) => n.lang === lang.toLowerCase());
      if (existing) {
        existing.value = value;
      } else {
        this.name.push(createLocalizedValue({ lang: lang.toLowerCase(), value }));
      }
    },

    getName(lang: string): string {
      return this.name.find((n) => n.lang === lang.toLowerCase())?.value || this.defaultName;
    },

    nameMatches(query?: string): boolean {
      if (query) {
        const clean = query
          .trim()
          .split(' ')
          .filter((q) => !!q.trim())
          .map((c) => c.toLowerCase());
        return clean.some((c) => this.name.some((n) => n.value.toLowerCase().includes(c)));
      }
      return true;
    },
  });
}
